import { createApi } from "@reduxjs/toolkit/query/react";
import type { Draft } from "immer";
import { ApiEndpoints, baseQueryWithAuth } from "../../core";
import { toast } from "../../core/toast";
import {
  SubscriberApiResponse,
  EditSubscriberRequest,
  Subscriber,
} from "../../types/subscriberManagement/subscriber/subscriber.interface";

type GetSubscribersArg = void | { page?: number; limit?: number };

export const subscriberApi = createApi({
  reducerPath: "subscriberApi",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["Subscriber", "Subscribers"],

  endpoints: (builder) => ({
    // Get all subscribers (with optional pagination)
    getSubscribers: builder.query<Subscriber[], GetSubscribersArg>({
      query: (arg) => {
        let page: number | undefined;
        let limit: number | undefined;

        if (arg && typeof arg === "object") {
          page = arg.page;
          limit = arg.limit;
        }

        const params = new URLSearchParams();
        if (page !== undefined) params.append("page", String(page));
        if (limit !== undefined) params.append("limit", String(limit));

        const queryString = params.toString();

        return {
          url: `${ApiEndpoints.subscriber}${queryString ? `?${queryString}` : ""
            }`,
          method: "GET",
        };
      },
      transformResponse: (res: any) => {
        return res?.results;
      },
      providesTags: ["Subscribers"],
    }),

    // Get subscriber by ID
    getSubscriberById: builder.query<Subscriber, string>({
      query: (subscriberid) => ({
        url: `${ApiEndpoints.getSubscriberById}/${subscriberid}`,
        method: "GET",
      }),
      transformResponse: (res: any): Subscriber => {
        return res?.result;
      },
      providesTags: (_result, _error, subscriberid) => [
        { type: "Subscriber", id: subscriberid },
      ],
    }),

    // Create subscriber
    createSubscriber: builder.mutation<
      SubscriberApiResponse<Subscriber>,
      EditSubscriberRequest
    >({
      query: (body) => ({
        url: `${ApiEndpoints.createSubscriber}`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Subscribers"],
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const msg =
            (data as any)?.message ||
            (data as any)?.result?.message ||
            "Subscriber created successfully";
          toast.success(msg);
        } catch (err: any) {
          const msg =
            err?.error?.data?.message ||
            err?.data?.message ||
            err?.error ||
            "Failed to create subscriber";
          toast.error(String(msg));
        }
      },
    }),

    // Edit subscriber
    editSubscriber: builder.mutation<
      SubscriberApiResponse<Subscriber>,
      { subscriberid: string; data: EditSubscriberRequest }
    >({
      query: ({ subscriberid, data }) => ({
        url: `${ApiEndpoints.editSubscriber}/${subscriberid}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, { subscriberid }) => [
        { type: "Subscriber", id: subscriberid },
        "Subscribers",
      ],
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const msg =
            (data as any)?.message ||
            (data as any)?.result?.message ||
            "Subscriber updated successfully";
          toast.success(msg);
        } catch (err: any) {
          const msg =
            err?.error?.data?.message ||
            err?.data?.message ||
            err?.error ||
            "Failed to update subscriber";
          toast.error(String(msg));
        }
      },
    }),

    // Delete subscriber with optimistic update
    deleteSubscriber: builder.mutation<
      SubscriberApiResponse<{ ok: boolean }>,
      string
    >({
      query: (subscriberid) => ({
        url: `${ApiEndpoints.deleteSubscriber}/${subscriberid}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Subscribers"],
      async onQueryStarted(subscriberid, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          subscriberApi.util.updateQueryData(
            "getSubscribers",
            undefined,
            (draft: Draft<Subscriber[]>) => {
              return draft.filter((s) => s.Subscriberid !== subscriberid);
            },
          ),
        );

        try {
          const { data } = await queryFulfilled;
          const msg =
            (data as any)?.message ||
            (data as any)?.result?.message ||
            "Subscriber deleted successfully";
          toast.success(msg);
        } catch (err: any) {
          patchResult.undo();
          const msg =
            err?.error?.data?.message ||
            err?.data?.message ||
            err?.error ||
            "Failed to delete subscriber";
          toast.error(String(msg));
        }
      },
    }),
    changeSubscriberStatus: builder.mutation<
      SubscriberApiResponse<{ ok: boolean }>,
      { subscriberid: string; status: boolean }
    >({
      query: ({ subscriberid, status }) => ({
        url: `${ApiEndpoints.subscriberStatusChange}/${subscriberid}`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: ["Subscribers"],
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const msg =
            (data as any)?.message ||
            (data as any)?.result?.message ||
            "Subscriber status changed successfully";
          toast.success(msg);
        } catch (err: any) {
          const msg =
            err?.error?.data?.message ||
            err?.data?.message ||
            err?.error ||
            "Failed to change subscriber status";
          toast.error(String(msg));
        }
      },
    }),
    getSubscriberDashboardToken: builder.mutation<
      SubscriberApiResponse<{ token: string }>,
      { subscriberid: string }
    >({
      query: ({ subscriberid }) => ({
        url: `${ApiEndpoints.baseSubscriberApi}/admin/subscriber/login/token/${subscriberid}`,
        method: "GET",
      }),
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err: any) {
          const msg =
            err?.error?.data?.message ||
            err?.data?.message ||
            err?.error ||
            "Failed to generate subscriber dashboard token";
          toast.error(String(msg));
        }
      },
    }),
  }),
});

export const {
  useGetSubscribersQuery,
  useGetSubscriberByIdQuery,
  useCreateSubscriberMutation,
  useEditSubscriberMutation,
  useDeleteSubscriberMutation,
  useChangeSubscriberStatusMutation,
  useGetSubscriberDashboardTokenMutation,
} = subscriberApi;
